import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { ApiError, unwrap } from '../api/api-error';
import type { components } from '../api/schema';

export type Profile = components['schemas']['Profile'];
export type AuthOptions = components['schemas']['AuthOptions'];

export const ME_KEY = ['me'] as const;

/**
 * Текущий пользователь: `null` — не вошёл (401). Источник правды о сессии — сервер:
 * cookie HttpOnly, скрипт её не видит (ADR-0003).
 */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<Profile | null> => {
      try {
        return unwrap(await api.GET('/api/v1/me'));
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function useAuthOptions() {
  return useQuery({
    queryKey: ['auth', 'options'],
    queryFn: async () => unwrap(await api.GET('/api/v1/auth/options')),
    staleTime: Infinity,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await api.POST('/api/v1/auth/logout');
      if (result.error !== undefined) unwrap(result);
    },
    onSettled: () => {
      // Данные прежнего пользователя не должны остаться в кэше.
      queryClient.clear();
    },
  });
}

/** Ответы 204/202 без тела: openapi-fetch отдаёт `data: undefined`, ошибкой это не считаем. */
export function ensureOk(result: { error?: unknown; response: Response }): void {
  if (result.error !== undefined || !result.response.ok) {
    unwrap({ ...result, data: undefined });
  }
}
